// The Deadbolts' reads and presses, shared by the desktop Doors page (pages/DoorLocksSection.jsx) and the phone Cameras
// screen (mobile/screens/cameras/PhoneLocks.jsx). Layout stays with each screen; `ui` is the screen's own dialogs
// (desktopUi / phoneUi), so a phone never calls window.confirm.

import { useCallback, useEffect, useRef, useState } from 'react';
import { needsAdminPin } from '../../utils/securityApi';
import { usePolling } from '../../components/cameras/useCameraData';
import {
  LOCKS_POLL_MS,
  LOCK_FOLLOW_MAX_MS,
  LOCK_FOLLOW_MS,
  commandOutcomeText,
  fetchLocks,
  sendLockCommand,
  setAutoLock,
} from './locks';

/** The locks, read every 30 s, every 2 s while a press is under way. */
export function useDeadbolts({ active = true, onRefused }) {
  const [data, setData] = useState(null);
  const [readFailed, setReadFailed] = useState(false);
  const [following, setFollowing] = useState(false);
  const followers = useRef(new Set());
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => { live.current = false; };
  }, []);

  const load = useCallback(async () => {
    const r = await fetchLocks();
    if (!live.current) return;
    if (needsAdminPin(r)) {
      if (onRefused) onRefused(r.message);
      return;
    }
    const good = Boolean(r.ok && r.body);
    setReadFailed(!good);
    if (good) setData(r.body);
  }, [onRefused]);

  usePolling(load, following ? LOCK_FOLLOW_MS : LOCKS_POLL_MS, active);

  const follow = useCallback((lockId, on) => {
    if (on) followers.current.add(lockId);
    else followers.current.delete(lockId);
    setFollowing(followers.current.size > 0);
  }, []);

  return { data, readFailed, load, follow };
}

/** One lock's Lock / Unlock (with a PIN typed for that press) and auto-lock switch, followed to the end. */
export function useLockControls({ lock, data, ui, onChanged, onFollow, onRefused }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [tracking, setTracking] = useState(null);     // { commandId, until }

  useEffect(() => {
    if (!tracking) return undefined;
    const c = ((data && data.commands) || []).find((x) => x.command_id === tracking.commandId);
    const done = commandOutcomeText(c, lock.name);
    if (done) {
      setMsg(done);
      setTracking(null);
      setBusy(false);
      return undefined;
    }
    // A timer as well as the reads: reads that fail must not leave the buttons disabled.
    const t = setTimeout(() => {
      setMsg({ kind: 'warning', text: 'No answer yet. Check the door, then the state above.' });
      setTracking(null);
      setBusy(false);
    }, Math.max(0, tracking.until - Date.now()));
    return () => clearTimeout(t);
  }, [data, tracking, lock.name]);

  useEffect(() => {
    onFollow(lock.lock_id, Boolean(tracking));
    return () => onFollow(lock.lock_id, false);
  }, [tracking, lock.lock_id, onFollow]);

  const press = async (action) => {
    const word = action === 'lock' ? 'Lock' : 'Unlock';
    const pin = await ui.pin(`${word} ${lock.name}: enter YOUR Admin PIN:`, { autofill: false });
    if (!pin) return;
    setBusy(true);
    setMsg(null);
    let r = await sendLockCommand(lock.lock_id, action, pin);
    if (r.status === 409 && action === 'lock' && /reads open/.test(r.message || '')
        && await ui.confirm('The door sensor reads open, and locking an open door jams the bolt. Lock anyway?')) {
      r = await sendLockCommand(lock.lock_id, action, pin, true);
    }
    if (!r.ok) {
      setBusy(false);
      setMsg({ kind: 'danger', text: r.message || 'JobQ refused that.' });
      return;
    }
    setMsg({ kind: 'info', text: `${action === 'lock' ? 'Locking' : 'Unlocking'}… this usually takes about 10 seconds.` });
    setTracking({ commandId: r.body.command.command_id, until: Date.now() + LOCK_FOLLOW_MAX_MS });
    onChanged();
  };

  const toggleAuto = async () => {
    setBusy(true);
    setMsg(null);
    const r = await setAutoLock(lock.lock_id, !lock.auto_lock);
    setBusy(false);
    if (needsAdminPin(r)) {
      if (onRefused) onRefused(r.message);
      return;
    }
    if (!r.ok) {
      setMsg({ kind: 'danger', text: r.message });
      return;
    }
    onChanged();
  };

  return { busy, msg, press, toggleAuto };
}
